import React, { useEffect, useState, useCallback, useRef } from 'react';
import { clsx } from 'clsx';
import { useTranslation, formatCount, pluralize } from '@/i18n';
import { apiClient, ContextUtilizationResponse } from '@/utils/apiClient';
import { useAppStore, whenSessionConfirmed, storeGeneration } from '@/stores/appStore';

interface ContextIndicatorProps {
  sessionId?: string;
  className?: string;
}

export const ContextIndicator: React.FC<ContextIndicatorProps> = ({
  sessionId,
  className,
}) => {
  const { t, language } = useTranslation();
  const [data, setData] = useState<ContextUtilizationResponse | null>(null);
  // STORY-290: refetch when an answer has been recorded (bumped by
  // refreshSessionStats after every answer), not on a timer guessed from the
  // message count, which could fire while the answer was still being generated.
  const statsRevision = useAppStore((state) => state.sessionStatsRevision);

  // The wait below can outlast a switch to another chat: drop a late answer.
  const currentId = useRef(sessionId);
  currentId.current = sessionId;

  const fetchContext = useCallback(async () => {
    if (!sessionId) return;
    try {
      // STORY-376: a new chat's id is unknown to the server until its create
      // has answered (404 before then); a failed create has nothing to show.
      // A logout during the wait leaves no token (401, which re-runs logout)
      // or the next user's (404): drop the fetch.
      const generation = storeGeneration();
      if (!(await whenSessionConfirmed(sessionId)) || generation !== storeGeneration()) return;
      const result = await apiClient.getContextUtilization(sessionId);
      if (currentId.current === sessionId) setData(result);
    } catch {
      // Silently fail — indicator just won't show
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    fetchContext();
  }, [sessionId, statsRevision, fetchContext]);

  if (!data || data.max_context_tokens === 0) return null;

  const pct = data.utilization_percent;
  const barColor =
    pct >= 80
      ? 'bg-red-500 dark:bg-red-400'
      : pct >= 60
      ? 'bg-yellow-500 dark:bg-yellow-400'
      : 'bg-green-500 dark:bg-green-400';

  const trackColor =
    pct >= 80
      ? 'bg-red-100 dark:bg-red-900/30'
      : pct >= 60
      ? 'bg-yellow-100 dark:bg-yellow-900/30'
      : 'bg-green-100 dark:bg-green-900/30';

  const counts = t.analytics.counts;
  const turnsLabel = formatCount(language, data.current_turns, counts.turns);
  const tokensLabel = pluralize(language, data.max_context_tokens, counts.tokens);
  const tooltipText =
    language === 'ru'
      ? `Контекст: ${data.estimated_context_tokens.toLocaleString()} / ${data.max_context_tokens.toLocaleString()} ${tokensLabel} (${pct.toFixed(1)}%) · ${turnsLabel}`
      : `Context: ${data.estimated_context_tokens.toLocaleString()} / ${data.max_context_tokens.toLocaleString()} ${tokensLabel} (${pct.toFixed(1)}%) · ${turnsLabel}`;

  return (
    <div
      className={clsx('group relative', className)}
      title={tooltipText}
    >
      {/* Thin bar */}
      <div className={clsx('h-1 w-full rounded-full overflow-hidden', trackColor)}>
        <div
          className={clsx('h-full rounded-full transition-all duration-500 ease-out', barColor)}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>

      {/* Hover tooltip overlay */}
      <div className="absolute left-0 right-0 -bottom-8 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
        <div className="mx-auto w-fit px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-[10px] rounded shadow-lg whitespace-nowrap">
          {language === 'ru' ? 'Контекст' : 'Context'}: {data.estimated_context_tokens.toLocaleString()} / {data.max_context_tokens.toLocaleString()} ({pct.toFixed(1)}%) · {turnsLabel}
        </div>
      </div>
    </div>
  );
};
